/**
 * ============================================================================
 *  AMPLIFY-INTERNAL TOKEN PROVIDER — DO NOT REUSE FOR APPLICATION AUTH LOGIC
 * ============================================================================
 *
 * This module exists for ONE purpose only: to feed AWS Amplify's
 * `fetchAuthSession()` (and therefore the `userPool`-mode Amplify Data
 * client `amplifyBackendClient.models.*`) with a Cognito JWT when the user
 * is on an opaque-token auth flow (`jwt-as:`, `pat:`).
 *
 * It is wired into `Amplify.configure(..., { Auth: { tokenProvider } })`
 * inside `configure.ts` and MUST NOT be used anywhere else.
 *
 * The single export `_DO_NOT_USE_OUTSIDE_AMPLIFY_tokenProvider` is only
 * consumed by Amplify itself via `Amplify.configure(...)`. The name
 * intentionally screams so that any reuse stands out in code review.
 * If you find yourself reaching for it, you are almost certainly doing
 * the wrong thing — talk to platform/auth first.
 *
 * Hard rules — non-negotiable:
 *
 *   1. The Cognito JWT produced here is for AppSync ONLY. It MUST NOT be
 *      sent to mobsuccess-app microservices via axios; those calls keep
 *      using the opaque token (`jwt-as:` / `pat:`).
 *
 *   2. The bridge JWT is fetched AT MOST ONCE per page load. We do NOT
 *      refresh it — `forceRefresh` is intentionally ignored on the alt
 *      flow. If the JWT expires mid-session, model calls will fail and
 *      that is the correct outcome: the user must re-bootstrap their
 *      session through the alt-flow, which will mint a fresh bridge JWT
 *      on the next page load.
 *
 *   3. For the impersonation flow (`jwt-as:`), `/generate-auth?output=jwt`
 *      returns the impersonatee's JWT, which is the desired behavior —
 *      model access uses the impersonated identity, so bugs can be
 *      reproduced exactly as the impersonated user sees them. Only the
 *      opaque token records the admin behind the impersonation.
 *
 * Why the restriction matters:
 *   The opaque token (`jwt-as:` / `pat:`) is the source of truth for
 *   non-Amplify calls. Re-minting or refreshing the Cognito JWT would
 *   create a parallel credential that drifts out of sync with the opaque
 *   token's lifecycle (logout, expiry, server-side flow detection).
 *
 * Neither symbol is re-exported from `./auth.ts` or
 * `@mobsuccess-devops/react-shared/auth` on purpose — keep it that way.
 * ============================================================================
 */
import { decodeJWT } from "aws-amplify/auth";
import type { AuthTokens, JWT } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

import { axiosPost } from "../fetch/query";

import { getOpaqueAuthorization } from "./session";

type GetTokensOptions = { forceRefresh?: boolean };

type GenerateAuthResponse = {
  Authorization: string;
  IdToken?: string;
  ExpiredAt: string;
};

let cachedTokens: AuthTokens | null = null;
let inFlight: Promise<AuthTokens | null> | null = null;
// Latched when the backend returns a verdict that cannot change for the rest of
// this page: 409 because the session is impersonating (see auth.php, which
// refuses to hand out the admin's bare JWT), or 401 on an expired PAT. Only the
// success path writes `cachedTokens`, so without this every Amplify call would
// re-issue generate-auth and get the same answer — the same unbounded-retry
// shape that made a single bad token produce 571 Cognito calls.
//
// Deliberately not latched on 5xx, 408, 429 or a network failure: those are
// transient, and latching would kill a session that the next call would have
// recovered. See `isPermanentRefusal`.
let refusedPermanently = false;

async function fetchGeneratedTokens(): Promise<AuthTokens> {
  // generate-auth requires POST — see auth.php::handleGenerate (returns 405
  // on anything else). Pass the opaque bearer explicitly rather than relying
  // on the axios default, since Amplify may call us before AuthProvider's
  // useSession has had a chance to run axiosInstances.authorize().
  // output=jwt makes the backend respond with a raw Cognito JWT whatever
  // the auth flow (jwt, jwt-as, pat) — auth tokens are opaque to the front,
  // we never parse them here.
  const opaque = getOpaqueAuthorization();
  const { data } = await axiosPost<GenerateAuthResponse>({
    entity: "auth",
    verb: "generate",
    params: { output: "jwt" },
    token: opaque ? `Bearer ${opaque}` : null,
    // generate-auth IS the refresh mechanism for this flow, so it must never
    // trigger AuthProvider's catch-401 interceptor: that awaits refreshSession()
    // -> fetchAuthSession({forceRefresh:true}) -> this very provider ->
    // `getGeneratedTokensOnce` -> the still-pending `inFlight` promise. The await
    // cycle never settles, so an expired PAT hangs every Amplify call and wedges
    // both `runningRequests[url]` and `refreshSessionPromise` for the page.
    // `doNotRetry` makes catch-401 rethrow, so the 401 reaches `mintTokens` and
    // latches as documented below.
    doNotRetry: true,
  });
  // `Authorization` is the access token — what AppSync wants for `userPool`
  // auth mode. `IdToken` is only present on backends that support it, and is
  // the one Amplify puts in the Cognito identity pool's `Logins` map.
  //
  // When it is absent we leave `idToken` unset rather than falling back to the
  // access token: an access token in that slot is not merely useless, it makes
  // every `GetId` fail with `NotAuthorizedException: Invalid login token`, and
  // nothing rate-limits that retry. Unset simply means no identity-pool
  // credentials on this flow, which is the honest state of affairs.
  const accessToken: JWT = decodeJWT(stripJwtScheme(data.Authorization));
  const idToken = data.IdToken
    ? decodeJWT(stripJwtScheme(data.IdToken))
    : undefined;
  cachedTokens = idToken ? { accessToken, idToken } : { accessToken };
  return cachedTokens;
}

// The platform tags the token it returns with the same `jwt:` scheme prefix it
// uses on its own Authorization header, so `output=jwt` responds with
// `jwt:eyJraWQi…` rather than a bare JWT.
//
// `decodeJWT` only base64-decodes the payload segment, which the prefix leaves
// untouched — so it accepts the prefixed string and its `toString()` returns it
// verbatim, prefix included. Amplify then hands that straight to Cognito, which
// rejects it with `NotAuthorizedException: Invalid login token. Not a valid
// OpenId Connect identity token.` Strip the prefix so what Amplify forwards is
// the JWT and nothing else.
function stripJwtScheme(token: string): string {
  return token.replace(/^jwt:/, "");
}

async function getGeneratedTokensOnce(): Promise<AuthTokens | null> {
  if (cachedTokens) {
    return cachedTokens;
  }
  if (refusedPermanently) {
    return null;
  }
  if (inFlight) {
    return inFlight;
  }
  inFlight = mintTokens();
  return inFlight;
}

async function mintTokens(): Promise<AuthTokens | null> {
  try {
    return await fetchGeneratedTokens();
  } catch (error) {
    if (!isPermanentRefusal(error)) {
      throw error;
    }
    // Report no session rather than rethrowing: Amplify handles a null token
    // as "not signed in", which is the accurate description of a flow that
    // cannot obtain a Cognito credential at all.
    refusedPermanently = true;
    return null;
  } finally {
    inFlight = null;
  }
}

function isPermanentRefusal(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return false;
  }
  const { response } = error;
  if (
    typeof response !== "object" ||
    response === null ||
    !("status" in response)
  ) {
    return false;
  }
  const { status } = response;
  // 408 and 429 are the only 4xx a retry can clear — everything else in the
  // range is a verdict about this session that will not change before reload.
  // The asymmetry decides the shape: failing to latch a permanent status brings
  // back the request storm, while latching a transient one only costs Amplify
  // model access until the next page load. Note that generate-auth goes through
  // the bare `PlatformMSAxiosInstance`, which has no 429 backoff of its own.
  return (
    typeof status === "number" &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}

export function _resetMsTokenProviderCache(): void {
  cachedTokens = null;
  inFlight = null;
  refusedPermanently = false;
}

export const _DO_NOT_USE_OUTSIDE_AMPLIFY_tokenProvider = {
  async getTokens(options: GetTokensOptions = {}): Promise<AuthTokens | null> {
    if (getOpaqueAuthorization()) {
      // forceRefresh is intentionally ignored — see rule 2 in the file
      // header. The bridge JWT is minted once per page load.
      return getGeneratedTokensOnce();
    }
    const { forceRefresh = false } = options;
    return cognitoUserPoolsTokenProvider.getTokens({ forceRefresh });
  },
};
